import type { Writable } from "svelte/store";
import * as filepath from "./filepath";
import { FS } from "./fs";
import type { YandexConfig } from "./fs";
import { FsDir } from "./fsentry";
import { xhrAsync, XhrOpts } from "./http";
import type { HttpProgress } from "./http";
import { sleep, throwIf } from "./util";

// https://yandex.com/dev/disk-api/doc/en/
// The Yandex Disk API answers other origins and allows the Authorization
// header, so the browser calls it directly and the token never reaches our
// server. For a file body the API hands out a one-time link on another Yandex
// host. We try that link from the browser first. If the browser refuses it
// (no CORS headers), the body goes through /api/yandex, which gets the link
// but not the token.

export const yandexBaseURL = "https://cloud-api.yandex.net/v1/disk";

export const prop_yandex_mime_type = "yandex_mime_type";
export const prop_yandex_md5 = "yandex_md5";

const listLimit = 1000;
const listFields = ["name", "type", "size", "modified", "mime_type", "md5"]
  .map((f) => "_embedded.items." + f)
  .concat("_embedded.total")
  .join(",");

// how long to wait for Yandex to finish a move, a delete or an upload
const operationTimeoutMs = 5 * 60 * 1000;
const operationPollMs = 1000;

interface YandexResource {
  name?: string;
  // "dir" or "file"
  type?: string;
  size?: number;
  // 2014-04-21T14:57:13+04:00
  modified?: string;
  mime_type?: string;
  md5?: string;
}

class FileSystemYandex extends FS {
  supportsDirRename = true;

  // false after the browser refused a link, so we ask it only once
  uploadDirect = true;
  downloadDirect = true;

  constructor(config: YandexConfig) {
    super(config);
  }

  get token(): string {
    return (this.config as YandexConfig).token;
  }

  call(method: string, path: string, params: Record<string, string>): Promise<any> {
    return yandexCall(this.token, method, yandexURL(path, params));
  }

  // Yandex answers 202 with a link to an operation when the work is not done yet.
  async callAndWait(method: string, path: string, params: Record<string, string>): Promise<void> {
    let js = await this.call(method, path, params);
    await this.waitForOperation(yandexOperationID(js.href));
  }

  async waitForOperation(id: string): Promise<void> {
    if (!id) {
      return;
    }
    let deadline = Date.now() + operationTimeoutMs;
    for (;;) {
      let js = await this.call("GET", "/operations/" + encodeURIComponent(id), {});
      if (js.status == "success") {
        return;
      }
      throwIf(js.status != "in-progress", "Yandex Disk could not finish the operation");
      throwIf(Date.now() > deadline, "Yandex Disk is taking too long to finish the operation");
      await sleep(operationPollMs);
    }
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let items: YandexResource[] = [];
      for (;;) {
        let js = await this.call("GET", "/resources", {
          path: filepath.stripDirSlash(dirPath),
          limit: String(listLimit),
          offset: String(items.length),
          fields: listFields,
        });
        let page = (js._embedded?.items || []) as YandexResource[];
        items.push(...page);
        let total = js._embedded?.total || 0;
        if (page.length == 0 || items.length >= total) {
          break;
        }
      }
      return FsDir.fromWire(dirPath, yandexItemsToWire(items));
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Fetch the file and hand the bytes back as a blob URL: a preview reads
    // the URL from this page, which the link's host may not allow.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let js = await this.call("GET", "/resources/download", { path: path });
    throwIf(!js.href, "Yandex Disk did not return a download link");
    let xhr = await this.transfer("download", js.href, (direct) => {
      let opts = new XhrOpts();
      opts.responseType = "blob";
      opts.httpProgress = httpProgress;
      if (!direct) {
        opts.headers = { "X-Yandex-Url": js.href };
      }
      return opts;
    });
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let js = await this.call("GET", "/resources/upload", { path: path, overwrite: "true" });
    throwIf(!js.href, "Yandex Disk did not return an upload link");
    await this.transfer("upload", js.href, (direct) => {
      let opts = new XhrOpts();
      opts.method = "PUT";
      opts.data = file;
      opts.headers = { "Content-Type": "application/octet-stream" };
      opts.httpProgress = httpProgress;
      if (!direct) {
        opts.headers["X-Yandex-Url"] = js.href;
      }
      return opts;
    });
    // Yandex stores the file after it answered the upload
    await this.waitForOperation(js.operation_id || "");
  }

  // Send or get a file body. A request the browser refuses ends with status 0
  // before any byte of the body moved, so the link is still good for the relay.
  async transfer(
    what: "upload" | "download",
    href: string,
    mkOpts: (direct: boolean) => XhrOpts,
  ): Promise<XMLHttpRequest> {
    let direct = what == "upload" ? this.uploadDirect : this.downloadDirect;
    try {
      if (direct) {
        try {
          return await xhrAsync(href, mkOpts(true));
        } catch (e: any) {
          if (e && e.status) {
            throw e;
          }
          if (what == "upload") {
            this.uploadDirect = false;
          } else {
            this.downloadDirect = false;
          }
        }
      }
      return await xhrAsync("/api/yandex/" + what, mkOpts(false));
    } catch (e: any) {
      if (e && e.status) {
        throw new Error(yandexStatusMsg(e.status, what));
      }
      throw new Error(`Yandex Disk ${what} failed`);
    }
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    await this.callAndWait("POST", "/resources/move", {
      from: filepath.stripDirSlash(path),
      path: filepath.stripDirSlash(newPath),
      overwrite: "false",
    });
  }

  async createDirectory(dirPath: string): Promise<void> {
    dirPath = filepath.stripDirSlash(dirPath);
    throwIf(dirPath == "/", "missing directory name");
    await this.call("PUT", "/resources", { path: dirPath });
  }

  // Yandex moves the entry to its trash. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    path = filepath.stripDirSlash(path);
    throwIf(path == "/", "cannot delete the root folder");
    await this.callAndWait("DELETE", "/resources", { path: path, permanently: "false" });
  }
}

export function yandexURL(path: string, params: Record<string, string>): string {
  let query = new URLSearchParams(params).toString();
  return yandexBaseURL + path + (query ? "?" + query : "");
}

// The id from an operation link like .../v1/disk/operations/33ca7d03ab21. We
// build the status url from the id, so the token goes only to the API host.
export function yandexOperationID(href: any): string {
  if (typeof href != "string") {
    return "";
  }
  let m = /\/operations\/([^/?#]+)/.exec(href);
  return m ? decodeURIComponent(m[1]) : "";
}

export function yandexItemsToWire(items: YandexResource[]): any[][] {
  let wires: any[][] = [];
  for (let it of items) {
    if (!it.name) {
      continue;
    }
    let isDir = it.type == "dir";
    let size = isDir ? -1 : it.size || 0;
    let lastMod = isDir ? 0 : Date.parse(it.modified || "") || 0;
    let meta: any[] = ["", it.name, size, lastMod, isDir ? [] : null];
    if (!isDir && it.mime_type) {
      meta.push(prop_yandex_mime_type, it.mime_type);
    }
    if (!isDir && it.md5) {
      meta.push(prop_yandex_md5, it.md5);
    }
    wires.push(meta);
  }
  return wires;
}

/**
 * Check the token. Returns the login of the account, empty if the token may
 * not read it.
 */
export async function yandexLogin(token: string): Promise<{ login: string }> {
  let js = await yandexCall(token, "GET", yandexURL("/", { fields: "user.login,user.display_name" }));
  return { login: js.user?.login || js.user?.display_name || "" };
}

function yandexStatusMsg(status: number, what: string): string {
  switch (status) {
    case 401:
      return "Yandex Disk: wrong or expired token";
    case 403:
      return "Yandex Disk: the token does not allow this";
    case 404:
      return "Yandex Disk: not found";
    case 409:
      return "Yandex Disk: an entry with this name already exists";
    case 413:
      return "Yandex Disk: the file is too large";
    case 423:
      return "Yandex Disk: the entry is locked or the disk is read-only";
    case 429:
      return "Yandex Disk: too many requests";
    case 507:
      return "Yandex Disk: not enough space";
  }
  return `Yandex Disk ${what} failed: ${status}`;
}

async function yandexCall(token: string, method: string, url: string): Promise<any> {
  let rsp: Response;
  try {
    rsp = await fetch(url, { method, headers: { Authorization: "OAuth " + token } });
  } catch {
    throw new Error("could not connect to Yandex Disk");
  }
  let text = await rsp.text();
  let js: any = {};
  try {
    js = text ? JSON.parse(text) : {};
  } catch {
    js = {};
  }
  if (!rsp.ok) {
    // description is English, message follows the account's language
    let msg = js?.description;
    if (typeof msg == "string" && msg != "" && rsp.status != 401) {
      throw new Error(`Yandex Disk: ${msg}`);
    }
    throw new Error(yandexStatusMsg(rsp.status, "request"));
  }
  return js;
}

export function makeFileSystemYandex(c: YandexConfig): FileSystemYandex {
  return new FileSystemYandex(c);
}
